import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BaseModal from '../modals/BaseModal/BaseModal'
import { getChatAnalyticsSchoolFilters, getChatAnalyticsSchoolStats } from '../../server/server'
import ChatStatsFilters from './chat-stats/ChatStatsFilter.jsx'
import ChatStatsOverview from './chat-stats/ChatStatsOverview'
import ChatStatsResponsiveness from './chat-stats/ChatStatsResponsiveness'
import ChatStatsAttention from './chat-stats/ChatStatsAttention'
import ChatStatsClasses from './chat-stats/ChatStatsClasses'
import ChatStatsSkeleton from './chat-stats/ChatStatsSkeleton'
import { CARD, SectionLabel } from './chat-stats/ChatStatsSection'

const EMPTY_FILTERS = {
    start_date: '',
    end_date: '',
    class_ids: [],
    types: [],
}

// overscrollBehavior keeps the wheel inside this panel: BaseModal's overlay is
// itself a scroller, and without containment reaching the end chains out to it.
const BODY_STYLE = { padding: '20px 30px 28px', minHeight: '60vh', maxHeight: 'min(80vh, 720px)', overflowY: 'auto', overscrollBehavior: 'contain' }

function ChatStatsModal({ isOpen, onClose }) {
    const { t } = useTranslation()
    const [filters, setFilters] = useState(EMPTY_FILTERS)
    const [options, setOptions] = useState({ classes: [] })
    const [stats, setStats] = useState(null)
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState(null)
    const [preset, setPreset] = useState('30d')

    useEffect(() => {
        if (!isOpen) return undefined
        let cancelled = false
        getChatAnalyticsSchoolFilters().then((response) => {
            if (cancelled || response?.status !== 200) return
            setOptions(response.data)
        })
        return () => {
            cancelled = true
        }
    }, [isOpen])

    useEffect(() => {
        if (!isOpen) return undefined
        let cancelled = false
        setLoading(true)
        setError(null)
        getChatAnalyticsSchoolStats(filters).then((response) => {
            if (cancelled) return
            if (response?.status === 200) setStats(response.data)
            else setError(response?.data?.message || t('chatStatsError'))
            setLoading(false)
        })
        return () => {
            cancelled = true
        }
    }, [isOpen, filters, t])

    return (
        <BaseModal isOpen={isOpen} onClose={onClose} maxWidth={1020} ariaLabel={t('chatStatsTitle')}>
            <div style={{ borderRadius: 10, overflow: 'hidden' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, padding: '24px 30px 20px', background: 'rgba(94, 99, 155, 0.13)' }}>
                    <div
                        style={{
                            width: 50,
                            height: 50,
                            borderRadius: 15,
                            background: '#fff',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: '#5e639b',
                        }}
                    >
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path d="M5 20V10M12 20V5M19 20v-7" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                        </svg>
                    </div>
                    <div>
                        <div style={{ fontSize: 22, fontWeight: 700, color: '#2d2b45' }}>{t('chatStatsTitle')}</div>
                        <div style={{ fontSize: 14, color: '#5e639b' }}>{t('chatStatsSubtitle')}</div>
                    </div>
                </div>

                <div style={BODY_STYLE}>
                    <ChatStatsFilters
                        filters={filters}
                        preset={preset}
                        classes={options.classes || []}
                        onPresetChange={(item) => {
                            setPreset(item.key)
                            if (item.range) setFilters({ ...filters, ...item.range() })
                        }}
                        onChange={setFilters}
                    />
                    {loading && <ChatStatsSkeleton />}
                    {error && <div style={{ color: '#b21f2b', fontWeight: 600 }}>{error}</div>}
                    {!loading &&
                        !error &&
                        stats &&
                        (stats.has_data ? (
                            <>
                                <ChatStatsOverview stats={stats} />
                                <ChatStatsResponsiveness stats={stats} />
                                <SectionLabel number={3}>{t('chatStatsSection3')}</SectionLabel>
                                <div style={{ display: 'flex', gap: 18, flexWrap: 'wrap' }}>
                                    <ChatStatsAttention stats={stats} />
                                    <ChatStatsClasses stats={stats} />
                                </div>
                            </>
                        ) : (
                            <div style={{ ...CARD, padding: '60px 24px', textAlign: 'center' }}>
                                <div style={{ fontSize: 18, fontWeight: 700, color: '#2d2b45' }}>{t('chatStatsEmptyTitle')}</div>
                                <div style={{ fontSize: 14, color: '#8e8fb4', maxWidth: 420, lineHeight: 1.5, margin: '8px auto 0' }}>{t('chatStatsEmptyBody')}</div>
                            </div>
                        ))}
                </div>
            </div>
        </BaseModal>
    )
}

// Chat re-renders on every socket message. Both props are stable while the modal
// is closed, so memo skips this whole subtree rather than rebuilding it each time.
export default React.memo(ChatStatsModal)
